import { Search, User, Heart, ShoppingBag, Menu, LogOut, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import logo from "@/assets/logo.png";
import { useCart } from "@/hooks/cart/CartProvider";
import { useAuth } from "@/hooks/useAuth";
import { useCategories } from "@/hooks/ecommerce/useCategories";
import CategoriesMegaMenu from "./header/CategoriesMegaMenu";

const StoreHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const { totals, openCart } = useCart();
  const { user, signOut } = useAuth();
  const { data: categories = [] } = useCategories();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") ?? "");

  useEffect(() => {
    setSearchTerm(searchParams.get("q") ?? "");
  }, [searchParams]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchTerm.trim();
    const target = q ? `/?q=${encodeURIComponent(q)}` : "/";
    navigate(target);
    setSearchOpen(false);
    // scroll to products section
    setTimeout(() => {
      const el = document.getElementById("produtos");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setMobileCatsOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between gap-4 lg:h-20">
        {/* Mobile menu toggle */}
        <button
          className="lg:hidden text-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" aria-label="Instituto Eu Salvo Vidas">
          <img src={logo} alt="Instituto Eu Salvo Vidas" className="h-12 w-12 lg:h-14 lg:w-14 object-contain" />
        </Link>

        {/* Search bar — desktop */}
        <div className="hidden flex-1 max-w-xl lg:flex">
          <form onSubmit={submitSearch} className="relative w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar produtos, categorias..."
              aria-label="Buscar produtos"
              className="h-10 w-full rounded-lg border border-border bg-muted/50 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </form>
        </div>

        {/* Icons */}
        <div className="flex items-center gap-1 lg:gap-2">
          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setSearchOpen(!searchOpen)}
            aria-label="Buscar"
          >
            <Search className="h-5 w-5" />
          </button>
          {user ? (
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:inline-flex text-foreground"
              onClick={() => signOut()}
              aria-label="Sair"
              title={`Sair (${user.email})`}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          ) : (
            <Button asChild variant="ghost" size="icon" className="hidden lg:inline-flex text-foreground" aria-label="Entrar">
              <Link to="/login">
                <User className="h-5 w-5" />
              </Link>
            </Button>
          )}
          <Button asChild variant="ghost" size="icon" className="text-foreground" aria-label="Meus favoritos">
            <Link to="/favoritos">
              <Heart className="h-5 w-5" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" className="relative h-11 w-11 text-foreground sm:h-10 sm:w-10" onClick={openCart} aria-label="Abrir carrinho">
            <ShoppingBag className="h-6 w-6 sm:h-5 sm:w-5" />
            {totals.itemsCount > 0 && (
              <span className="absolute right-0 top-0 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-accent text-[11px] font-bold text-accent-foreground sm:h-4 sm:w-4 sm:text-[10px]">
                {totals.itemsCount > 9 ? "9+" : totals.itemsCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile search */}
      {searchOpen && (
        <div className="border-t border-border px-4 py-3 lg:hidden">
          <form onSubmit={submitSearch} className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar produtos..."
              aria-label="Buscar produtos"
              className="h-10 w-full rounded-lg border border-border bg-muted/50 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              autoFocus
            />
          </form>
        </div>
      )}

      {/* Nav links — desktop */}
      <nav className="hidden border-t border-border lg:block">
        <div className="container flex items-center justify-center gap-8 py-3 text-sm font-medium text-muted-foreground">
          {[
            { label: "Início", href: "/" },
            { label: "Antes & Depois", href: "/antes-depois" },
            { label: "Mais Vendidos", href: "/mais-vendidos" },
          ].map((item) => (
            <NavLink
              key={item.label}
              to={item.href}
              end={item.href === "/"}
              className={({ isActive }) =>
                `transition-colors hover:text-primary ${isActive ? "text-primary" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <CategoriesMegaMenu />
          <NavLink
            to="/sobre"
            className={({ isActive }) =>
              `transition-colors hover:text-primary ${isActive ? "text-primary" : ""}`
            }
          >
            Sobre
          </NavLink>
        </div>
      </nav>

      {/* Mobile / Tablet drawer */}
      <Sheet open={mobileMenuOpen} onOpenChange={(o) => { if (!o) closeMobile(); else setMobileMenuOpen(true); }}>
        <SheetContent side="left" className="w-[85vw] max-w-sm p-0 lg:hidden">
          <SheetHeader className="border-b border-border px-4 py-3 text-left">
            <SheetTitle className="font-display text-lg font-semibold text-foreground">Menu</SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col overflow-y-auto py-2 text-sm">
              {[
                { label: "Início", href: "/" },
                { label: "Antes & Depois", href: "/antes-depois" },
                { label: "Mais Vendidos", href: "/mais-vendidos" },
                { label: "Favoritos", href: "/favoritos" },
                { label: "Sobre", href: "/sobre" },
              ].map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === "/"}
                  onClick={closeMobile}
                  className={({ isActive }) =>
                    `px-4 py-3 font-medium transition-colors hover:bg-muted ${
                      isActive ? "text-primary" : "text-foreground"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              {categories.length > 0 && (
                <div>
                  <button
                    onClick={() => setMobileCatsOpen((v) => !v)}
                    className="flex w-full items-center justify-between px-4 py-3 font-medium text-foreground transition-colors hover:bg-muted"
                  >
                    Coleções
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileCatsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileCatsOpen && (
                    <div className="border-t border-border bg-muted/30">
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/categoria/${cat.slug}`}
                          onClick={closeMobile}
                          className="block px-8 py-2.5 text-sm text-foreground transition-colors hover:bg-muted"
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="my-2 border-t border-border" />

              {user ? (
                <button
                  onClick={() => { signOut(); closeMobile(); }}
                  className="flex items-center gap-2 px-4 py-3 text-left font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <LogOut className="h-4 w-4" /> Sair
                </button>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMobile}
                  className="flex items-center gap-2 px-4 py-3 font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <User className="h-4 w-4" /> Entrar
                </Link>
              )}
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default StoreHeader;
